import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ChevronRight, ChevronLeft, Check } from 'lucide-react';

function ImageGallery({ images }) {
  const [currentIdx, setCurrentIdx] = useState(0);

  const nextImage = () => setCurrentIdx((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentIdx((prev) => (prev - 1 + images.length) % images.length);

  return (
    <div className="relative rounded-[8px] overflow-hidden h-96 md:h-[500px] order-2 md:order-1 group">
      <img
        src={images[currentIdx]}
        alt="Gallery"
        className="w-full h-full object-cover transition-opacity duration-300"
      />
      {images.length > 1 && (
        <>
          <button
            onClick={prevImage}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronRight size={20} />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {images.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIdx(idx)}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentIdx ? 'bg-white w-6' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function SaunaExterior() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeTab, setActiveTab] = useState(0);
  const [imageIdx, setImageIdx] = useState([0, 0, 0, 0]);

  const sections = [
    {
      title: 'Kledning & Fasade',
      subtitle: 'Kebony — tre for fremtiden',
      content: 'Kledningen på badstuen din fra Kebony er en intelligent løsning. Det modifiserte treet kombinerer hardhet av hardtré med bearbeidingsvennligheten til mykt tre. 30 års garanti uten maling eller olje.',
      features: [
        'Kebony Clear — glatt, moderne overflate',
        'Kebony Character — med knuter, rustikk',
        'Naturlig sølvgrå patina etter 1-2 år',
        'Nullvedlikehold i tiår',
        'Beskyttet mot UV, fuktighet og insekter'
      ],
      images: [
        'https://brookstimber.com/wp-content/uploads/2025/01/kebony25Body-1024x768.jpg',
        'https://brookstimber.com/wp-content/uploads/2021/10/kebonyClear-1.jpg',
        'https://brookstimber.com/wp-content/uploads/2021/10/kebonyCharacter-1.jpg'
      ],
      link: '/kebony-vedlikehold'
    },
    {
      title: 'Tak & Beskyttelse',
      subtitle: 'Fra toppen — maksimal sikkerhet',
      content: 'Taket er kritisk. Enten du velger grønt tak (LY), panoramavinduer (PANORAMA) eller universell løsning (FJEL) — alt er designet for tiår av nordiske forhold.',
      features: [
        'Vinduer av høy kvalitet (Sapa by Hydro)',
        'Hydraulikk for skyvebare vinduer',
        'Materialer bestandige mot frost og snø',
        'Drenering designet for Skandinavia',
        'Isolasjon som reduserer varmetap'
      ],
      images: [
        'https://cdn.prod.website-files.com/69675e688a8322c8f309fa2f/698e243f4aa5afdb516be1ee_toppmodul-ha%CC%8Andtverket.jpg',
        'https://cdn.prod.website-files.com/69675e688a8322c8f309fa2f/698e1f55bc0ff4a6e706ad0b_sapa.jpg',
        'https://cdn.prod.website-files.com/69675e688a8322c8f309fa2f/699c33d4787c4e0754ecd6d2_toppbanner-prosess-varanger.jpg'
      ],
      link: '/process-details'
    },
    {
      title: 'Strukturell Sikkerhet',
      subtitle: 'Fundamenter for generasjoner',
      content: 'Badstuen er en investering. SIP-teknologi (strukturelt isolerte paneler) og modulær konstruksjon sikrer holdbarhet og fleksibilitet. Alt beregnet for verste vilkår.',
      features: [
        'SIP-paneler — 50% lavere CO₂',
        'Trekonstruksjon uten fuger',
        'Dimensjoner tilpasset vind og snølast',
        'Aluminiumsvinduer (25 års garanti)',
        'TEK17-godkjent design'
      ],
      images: [
        'https://cdn.prod.website-files.com/69675e688a8322c8f309fa2f/698e2041ae201b793edd915b_konstruksja.jpg',
        'https://cdn.prod.website-files.com/69675e688a8322c8f309fa2f/698e17f4ad0a2e4c7d45c147_bygg-din-omgi.jpg',
        'https://cdn.prod.website-files.com/69675e688a8322c8f309fa2f/698e1ef12e27cc1af6dbe8c7_Fabrikken.jpg'
      ],
      link: '/handverket'
    },
    {
      title: 'Vedlikehold & Pleie',
      subtitle: 'Enkelt, sjelden, i tiår',
      content: 'Å ta vare på badstuen er en glede, ikke en byrde. Med Kebony trenger du bare av og til rengjøring. Slik holder du den i perfekt stand i generasjoner.',
      features: [
        'Årlig visuell inspeksjon',
        'Rengjøring med myk børste',
        'Unngå høytrykksspyling',
        'Vask fasaden hver 2-3 år',
        'Overvåk takdrenering'
      ],
      images: [
        'https://brookstimber.com/wp-content/uploads/2025/01/kebony25Thumb5.jpg',
        'https://brookstimber.com/wp-content/uploads/2025/01/kebony25Thumb4.jpg',
        'https://brookstimber.com/wp-content/uploads/2025/01/kebony25Body-1024x768.jpg'
      ],
      link: '/eksteriormaling'
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <div className="pt-20 pb-20">
        {/* Hero Section with Image */}
        <div className="relative h-[500px] md:h-[600px] mb-32 overflow-hidden rounded-[12px] mx-6 md:mx-12">
          <img
            src="https://cdn.prod.website-files.com/69675e688a8322c8f309fa2f/698e2041ae201b793edd915b_konstruksja.jpg"
            alt="Badstuen Utsiden"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent flex flex-col justify-end p-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-sm tracking-widest-2xl font-body uppercase text-white mb-6">Komplett Guide</p>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light leading-[1.1] text-white mb-6">
                Utsiden av
                <br />
                <span className="italic">Badstuen</span>
              </h1>
              <p className="font-body text-white/80 text-lg max-w-2xl leading-relaxed">
                Fra kledning til tak til vedlikehold — alt du trenger å vite for at badstuen din skal vare generasjoner.
              </p>
            </motion.div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 md:px-12">

          {/* Tabs Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-20 flex flex-wrap gap-3 md:gap-4"
          >
            {sections.map((section, idx) => (
              <motion.button
                key={idx}
                whileHover={{ y: -2 }}
                onClick={() => setActiveTab(idx)}
                className={`px-6 py-3 rounded-[6px] font-heading text-sm tracking-widest transition-all ${
                  activeTab === idx
                    ? 'bg-[#2a1810] text-white'
                    : 'border border-border/30 text-foreground hover:border-primary/50 hover:bg-secondary/30'
                }`}
              >
                {section.title.split(' ')[0]}
              </motion.button>
            ))}
          </motion.div>

          {/* Tab Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-32 grid md:grid-cols-2 gap-16 items-center bg-white rounded-[8px] p-8 md:p-12 border border-border/20"
          >
            {/* Left side - Image Gallery */}
            <ImageGallery images={sections[activeTab].images} />

            {/* Right side - Content */}
            <div className="order-1 md:order-2">
              <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-4">{sections[activeTab].subtitle}</p>
              <h2 className="font-heading text-3xl md:text-4xl font-light leading-[1.2] mb-6 text-[#2a1810]">{sections[activeTab].title}</h2>

              <p className="font-body text-foreground/70 leading-relaxed mb-8">
                {sections[activeTab].content}
              </p>

              <div className="space-y-3 mb-8">
                {sections[activeTab].features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <Check size={20} className="text-primary flex-shrink-0 mt-0.5" />
                    <span className="font-body text-foreground/70">{feature}</span>
                  </div>
                ))}
              </div>

              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href={sections[activeTab].link}
                className="inline-block px-8 py-3 bg-[#2a1810] text-white font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-[#2a1810]/90 transition-colors"
              >
                LES MER
              </motion.a>
            </div>
          </motion.div>

          {/* Integration Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-32"
          >
            <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-6 text-center">Integrert Løsning</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light leading-[1.1] mb-16 text-center text-[#2a1810]">Alt <span className="italic">integrert</span></h2>

            <div className="grid md:grid-cols-4 gap-6">
              {[
                { label: 'Materialer', desc: 'Kebony, aluminium, SIP-tre' },
                { label: 'Design', desc: 'TEK17-godkjent, tilpasset klima' },
                { label: 'Produksjon', desc: 'Norsk fabrikk, millimeter presisjon' },
                { label: 'Garanti', desc: '30 år på struktur og Kebony' }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ y: -8 }}
                  className="bg-white rounded-[8px] p-8 border border-border/20 hover:border-[#2d5a3d] transition-colors"
                >
                  <p className="font-heading text-sm tracking-widest uppercase text-primary mb-4">{item.label}</p>
                  <p className="font-body text-foreground/70">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* Timeline */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mb-32"
          >
            <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-6">Tidsplan</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light leading-[1.1] mb-16 text-[#2a1810]">Badstuen gjennom <span className="italic">årene</span></h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { year: 'År 1', title: 'Innflytting', items: ['Sluttinspeksjon', 'FDV-dokument', 'Rengjøring'] },
                { year: 'År 1-5', title: 'De første årene', items: ['Årlig inspeksjon', 'Myk rengj.', 'Overvåk drener.'] },
                { year: 'År 5-10', title: 'Stabilisering', items: ['Dypere rengj.', 'Kontroll vinduer', 'Patina dannes'] },
                { year: 'År 10-30', title: 'Modning', items: ['Minimal inngripen', 'Sjelden rengj.', 'Vakker patina'] }
              ].map((phase, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ y: -8 }}
                  className="bg-white rounded-[8px] border-l-4 border-[#2d5a3d] p-8 hover:shadow-md transition-all"
                >
                  <span className="text-3xl font-heading font-light text-[#2d5a3d]/60 block mb-3">{phase.year}</span>
                  <h3 className="font-heading text-lg font-light mb-4 text-[#2a1810]">{phase.title}</h3>
                  <ul className="space-y-2">
                    {phase.items.map((item, i) => (
                      <li key={i} className="font-body text-base text-foreground/70 flex items-start gap-2">
                        <Check size={14} className="text-[#2d5a3d] flex-shrink-0 mt-1" /> {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* FAQ-style tips */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mb-32"
          >
            <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-6">Tips</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light leading-[1.1] mb-16 text-[#2a1810]">Praktiske <span className="italic">tips</span></h2>

            <div className="space-y-4">
              {[
                { q: 'Må jeg male Kebony?', a: 'Nei. Kebony danner naturlig beskyttende patina. Hvis du vil bevare originalfargen, kan du bruke UV-beskyttelse, men det er valgfritt.' },
                { q: 'Hva gjør jeg på vinteren?', a: 'Fjern tung snø fra taket, sikre at drenering ikke er blokkert. Kebony tåler frost utmerket — det er dets naturlige miljø.' },
                { q: 'Hvordan rengjør jeg Kebony?', a: 'Bruk myk børste og mildt såpevann. Aldri høytrykksspyling — det kan ødelegge patina og skade treet.' }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ y: -4 }}
                  className="bg-white rounded-[8px] p-8 border border-border/20 hover:border-[#2d5a3d] transition-colors hover:shadow-md"
                >
                  <h3 className="font-heading text-lg font-light mb-3 text-[#2a1810]">{item.q}</h3>
                  <p className="font-body text-foreground/70">{item.a}</p>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* Related Links */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="mb-32"
          >
            <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-6">Ressurser</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light leading-[1.1] mb-16 text-[#2a1810]">Relaterte <span className="italic">sider</span></h2>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                { title: 'Kebony Vedlikehold', desc: 'Alt om Kebony-teknologi og pleie', link: '/kebony-vedlikehold' },
                { title: 'Håndverket', desc: 'Fra norsk tradisjon til moderne produksjon', link: '/handverket' },
                { title: 'Eksteriormaling', desc: 'Beskyttelse og omsorg for badstuen', link: '/eksteriormaling' }
              ].map((item, idx) => (
                <motion.a
                  key={idx}
                  whileHover={{ y: -8 }}
                  href={item.link}
                  className="bg-white rounded-[8px] p-8 border border-border/20 hover:border-[#2d5a3d] transition-colors group hover:shadow-md"
                >
                  <h3 className="font-heading text-lg font-light mb-3 text-[#2a1810] group-hover:text-[#2d5a3d] transition-colors">{item.title}</h3>
                  <p className="font-body text-base text-foreground/70 mb-6">{item.desc}</p>
                  <div className="flex items-center gap-2 text-[#2d5a3d] font-body text-sm tracking-widest group-hover:translate-x-1 transition-transform">
                    LES <ChevronRight size={16} />
                  </div>
                </motion.a>
              ))}
            </div>
          </motion.section>

          {/* CTA */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="bg-gradient-to-br from-[#2a1810] to-[#3d2818] text-white rounded-[6px] p-12 md:p-20 text-center"
          >
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
              <p className="text-sm tracking-widest-2xl font-body uppercase text-white/60 mb-6">Neste Steg</p>
              <h2 className="font-heading text-3xl md:text-4xl font-light leading-[1.2] mb-6">
                Klar for Din
                <br />
                <span className="italic">Badstue?</span>
              </h2>
              <p className="font-body text-lg text-white/70 mb-12 max-w-2xl mx-auto">
                Nå vet du hva som ventet. La eksperten veilede deg gjennom resten av prosessen.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href="/sauna-umi-konfigurator"
                  className="px-8 py-4 bg-white text-[#2a1810] font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-white/90 transition-all inline-block"
                >
                  KONFIGURER BADSTUE
                </motion.a>
                <motion.a
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href="/be-om-tilbud"
                  className="px-8 py-4 border-2 border-white text-white font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-white/10 transition-all inline-block"
                >
                  BE OM TILBUD
                </motion.a>
              </div>
            </motion.div>
          </motion.section>
        </div>
      </div>

      <Footer />
    </div>
  );
}